import classNames from 'classnames';
import React from 'react';
import { InputNumber, Tooltip } from '@meitu/saas-ui';
import IconFont from '@meitu/xdesign-icon-font';
import LabelAction from '@/components/label-action';
import { useEditor } from '@/hooks/use-editor';
import { useLocaleStore } from '@/i18n';
import './index.less';

interface IProps {
    id: string;
    rotate: number;
    lock: boolean;
    disableFlipBtn?: boolean;
    onRotate?: () => void;
    onFlip?: (flip: 'horizontal' | 'vertical') => void;
}

const AngleInspector: React.FC<IProps> = (props) => {
    const { id, rotate, lock, disableFlipBtn, onFlip, onRotate } = props;

    const { $t } = useLocaleStore();
    const editor = useEditor();

    const __disableFlipBtn = disableFlipBtn || lock;
    const setRotate = (val: number | null, options?: { writeHistory: boolean }) => {
        if (val === null) return;
        const rotate = ((val % 360) + 360) % 360;
        editor.layer.modifyBasicProperties.rotate(id, rotate, options);
    };

    const getActualAngle = (angle: number) => {
        let newRotate = angle % 360;
        if (newRotate < 0) {
            newRotate += 360;
        }
        return ~~(newRotate <= 180 ? newRotate : -(360 - newRotate));
    };

    return (
        <div className="c-base-toolbar-controller">
            <LabelAction title={$t((v) => v.baseToolbar.angle)} align>
                <InputNumber
                    style={{ width: 53, flex: 'initial', marginRight: 4 }}
                    value={getActualAngle(rotate)}
                    showStepBtn={false}
                    disabled={lock}
                    onChange={(val: number | null) => {
                        setRotate(val, { writeHistory: false });
                    }}
                    onChangeAfter={(val: number | null) => {
                        setRotate(val, { writeHistory: true });
                        onRotate?.();
                    }}
                    suffix={<span>°</span>}
                />

                <div className="c-base-toolbar-controller-action">
                    <Tooltip
                        placement="bottom"
                        title={$t((v) => v.baseToolbar.rotate90)}
                        getPopupContainer={(el) => el.parentNode as HTMLElement}
                    >
                        <div
                            className={classNames('c-base-toolbar-controller-item', {
                                disabled: lock,
                            })}
                            onClick={() => {
                                if (lock) return;
                                setRotate(rotate + 90, { writeHistory: true });
                                onRotate?.();
                            }}
                        >
                            <IconFont name="Rotate90ClockwiseBold" fontSize={14} />
                        </div>
                    </Tooltip>

                    <Tooltip
                        placement="bottom"
                        title={$t((v) => v.baseToolbar.flipHorizontal)}
                        getPopupContainer={(el) => el.parentNode as HTMLElement}
                    >
                        <div
                            className={classNames('c-base-toolbar-controller-item', {
                                disabled: __disableFlipBtn,
                            })}
                            onClick={() => {
                                if (__disableFlipBtn) return;
                                editor.layer.flipX(id);
                                onFlip?.('horizontal');
                            }}
                        >
                            <IconFont name="FlipHorizontalTriangleBold" fontSize={14} />
                        </div>
                    </Tooltip>

                    <Tooltip
                        placement="bottom"
                        title={$t((v) => v.baseToolbar.flipVertical)}
                        getPopupContainer={(el) => el.parentNode as HTMLElement}
                    >
                        <div
                            className={classNames('c-base-toolbar-controller-item', {
                                disabled: __disableFlipBtn,
                            })}
                            onClick={() => {
                                if (__disableFlipBtn) return;
                                editor.layer.flipY(id);
                                onFlip?.('vertical');
                            }}
                        >
                            <IconFont name="FlipVerticalTriangleBold" fontSize={14} />
                        </div>
                    </Tooltip>
                </div>
            </LabelAction>
        </div>
    );
};

export default React.memo(AngleInspector);
